/* ============================ PROXMOX CLUSTERS ============================ */
import { useEffect, useState } from "react";
import { DB, api, can, go, job, node, toast, wl } from "../lib/store";
import { ago } from "../lib/fmt";
import { Badge, Chips, Copyable } from "../ui/common";
import { Btn, F, FormCtx, Frame, Modal, startWizard, useForm, type Form } from "../ui/modal";
import { PAGES } from "./registry";

export const clusterOf = (n: any): any => (n && n.cluster_id ? DB.clusters.find((c: any) => c.id === n.cluster_id) || null : null);
export const haOn = (w: any): boolean => {
  if (!w) return false;
  const c = clusterOf(node(w.node));
  if (!c) return false;
  const sid = `${w.type === "vm" ? "vm" : "ct"}:${w.vmid}`;
  return (c.ha?.resources || []).some((r: any) => r.sid === sid);
};
const linkOpts = (n: any): [string, string][] =>
  ((n && n.link_ips) || []).map((x: any) => [x.ip, `${x.ip} · ${x.iface || ""}${x.overlay ? " · Tailscale (not recommended)" : ""}`]);
const defaultLink = (n: any): string => {
  if (!n) return "";
  const ips = n.link_ips || [];
  return (ips.find((x: any) => !x.overlay) || ips[0] || { ip: n.lan && n.lan !== "—" ? n.lan : "" }).ip;
};
const guestsOn = (n: any) => DB.workloads.filter((w: any) => w.node === n.id).length;

/* ---------- page ---------- */
function ClusterCard({ c }: { c: any }) {
  const ad = can("Admin");
  const ns = DB.nodes.filter((n: any) => n.cluster_id === c.id);
  const members = c.members || [];
  const online = members.filter((m: any) => m.online).length;
  const res = c.ha?.resources || [];
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="hd"><h3>{c.name}</h3>
        <span className={"badge " + (c.quorate ? "ok" : "bad")} style={{ marginLeft: 8 }}><span className="dot" />{c.quorate ? "quorate" : "no quorum"}</span>
        <span className="badge" style={{ marginLeft: 6 }}>{online}/{members.length} online</span>
        {c.ha && c.ha.manager ? <span className="badge" style={{ marginLeft: 6 }}>HA manager: {c.ha.manager}</span> : null}
        <div className="spacer" />
        {ad ? <button className="btn sm" onClick={() => joinCluster(c.id)}>Add device…</button> : null}
      </div>
      <div className="bd" style={{ padding: 0 }}>
        <table><thead><tr><th>Member</th><th>Node ID</th><th>Corosync address</th><th>State</th><th>Managed here</th><th>Guests</th><th></th></tr></thead><tbody>
          {members.map((m: any) => {
            const n = ns.find((x: any) => (x.pve_node || x.name) === m.name);
            return (
              <tr key={m.name} className="row">
                <td><b style={{ fontWeight: 500 }}>{m.name}</b></td><td className="mono">{m.nodeid}</td><td className="mono">{m.ip || "—"}</td>
                <td>{m.online ? <span className="badge ok"><span className="dot" />online</span> : <span className="badge bad"><span className="dot" />offline</span>}</td>
                <td>{n ? <><a style={{ cursor: "pointer" }} onClick={() => go("node", n.id)}>{n.name}</a> <Badge s={n.status} /></>
                  : <span className="muted">not claimed in this console</span>}</td>
                <td>{n ? guestsOn(n) : "—"}</td>
                <td style={{ textAlign: "right" }}>{ad && n && members.length > 1
                  ? <button className="btn sm ghost" onClick={() => leaveCluster(n.id)}>Remove from cluster…</button> : null}</td>
              </tr>
            );
          })}
        </tbody></table>
        {res.length ? (
          <div style={{ padding: "12px 16px", borderTop: "1px solid var(--line-2)" }}><b style={{ fontWeight: 500 }}>HA resources</b>{" "}
            {res.map((r: any) => <span key={r.sid} className="badge" style={{ marginLeft: 6 }}>{r.sid} · {r.state || r.status || ""} on {r.node || ""}</span>)}</div>
        ) : null}
        <div className="hint" style={{ padding: "8px 16px 12px" }}>Last read {ago(c.last_sync)} · config version {c.version} · {c.created_by_console ? "created from this console" : "discovered from the devices"}
          {members.length === 2 ? <> · <b>two nodes: add a third node or a QDevice before relying on HA</b></> : null}</div>
      </div>
    </div>
  );
}

function ClustersPage() {
  const standalone = DB.nodes.filter((n: any) => !n.cluster_id);
  const ad = can("Admin");
  return (
    <>
      <div className="pagehead">
        <div><h2>Proxmox clusters</h2>
          <Chips items={[[DB.clusters.length, "clusters"], [DB.nodes.filter((n: any) => n.cluster_id).length, "clustered devices"], [standalone.length, "standalone"]]} />
          <p>Group devices into a Proxmox VE cluster (corosync) for one management plane, live migration and high availability.
            Devices at different sites usually stay standalone: corosync needs a fast, low-latency link (&lt; 5 ms).</p></div>
        <div className="spacer" />
        {ad ? <button className="btn primary" onClick={() => newCluster()}>Create cluster</button> : null}
      </div>
      {DB.clusters.length ? DB.clusters.map((c: any) => <ClusterCard key={c.id} c={c} />)
        : <div className="empty" style={{ marginBottom: 16 }}><h3>No clusters yet</h3><p>Every device is standalone. Create a cluster to migrate guests between devices and to use HA.</p></div>}
      <div className="section-title">Standalone devices</div>
      <div className="tbl-wrap"><table><thead><tr><th>Device</th><th>PVE</th><th>Arch</th><th>Guests</th><th>Addresses</th><th></th></tr></thead><tbody>
        {standalone.length ? standalone.map((n: any) => (
          <tr key={n.id} className="row">
            <td><a style={{ cursor: "pointer" }} onClick={() => go("node", n.id)}><b style={{ fontWeight: 500 }}>{n.name}</b></a> <Badge s={n.status} /></td>
            <td className="mono">{n.pve_version || "?"}</td><td>{n.arch || "?"}</td><td>{guestsOn(n)}</td>
            <td className="mono" style={{ fontSize: 12 }}>{(n.link_ips || []).map((x: any) => x.ip).join(", ") || "—"}</td>
            <td style={{ textAlign: "right" }}>{ad && DB.clusters.length
              ? <button className="btn sm" onClick={() => joinCluster(DB.clusters[0].id, n.id)}>Add to a cluster…</button> : null}</td>
          </tr>
        )) : <tr><td colSpan={6} className="muted">Every device is in a cluster.</td></tr>}
      </tbody></table></div>
    </>
  );
}
PAGES.clusters = { crumb: () => "Clusters", C: ClustersPage };

/* ---------- preflight results ---------- */
function Checks({ r }: { r: any }) {
  if (!r) return null;
  if (r.pending) return <div className="vres"><div className="vh"><span className="spin" /> Checking every device… (API, SSH, versions, guests, clocks, latency)</div></div>;
  if (r.error) return <div className="vres"><div className="vh" style={{ background: "var(--bad-soft)", color: "var(--bad)" }}>{r.error}</div></div>;
  return (
    <div className="vres">
      <div className="vh" style={{ background: r.ok ? "var(--ok-soft)" : "var(--bad-soft)", color: r.ok ? "var(--ok)" : "var(--bad)" }}>
        {r.ok ? "Preflight passed — nothing has been changed yet" : "Preflight found problems — nothing has been changed; fix the red lines first"}</div>
      <ul>{(r.checks || []).map((c: any, i: number) => (
        <li key={i}><span className={"tag " + c.tag}>{c.tag === "ok" ? "ok" : c.tag === "wrn" ? "warn" : "error"}</span>
          <span><b style={{ fontWeight: 500 }}>{c.node}</b> — {c.msg}</span></li>
      ))}</ul>
    </div>
  );
}
/* a preflight step: runs once when first shown (old step.after), again on demand */
function PreflightStep({ f, run, intro }: { f: Form; run: (f: Form) => Promise<void>; intro?: boolean }) {
  useEffect(() => { if (!f.d._pf) run(f); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {intro ? <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Read-only checks on every device: API and root SSH, Proxmox version, CPU architecture, existing cluster membership, guests on joining devices, hostnames, storage definitions, clock sync, and round-trip time between the corosync addresses.</p> : null}
      <div className="btnrow" style={{ marginBottom: 12 }}><button className="btn" onClick={() => run(f)}>Run preflight again</button></div>
      <div id="pfbox"><Checks r={f.d._pf} /></div>
    </>
  );
}
const pfGate = (msg: string) => (d: any) => (!d._pf || d._pf.pending ? "Wait for the preflight to finish." : !d._pf.ok ? msg : null);

/* corosync address picker for one device (select when the agent reported addresses, text otherwise) */
function LinkField({ f, n, label, resetPf = true, ph }: { f: Form; n: any; label: string; resetPf?: boolean; ph?: string }) {
  const o = linkOpts(n);
  const cur = f.d.links[n.id] ?? "";
  const set = (v: string) => { f.d.links[n.id] = v; if (resetPf) f.d._pf = null; f.refresh(); };
  return (
    <label className="f"><span>{label}</span>
      {o.length
        ? <select value={cur} onChange={(e) => set(e.target.value)}>{o.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>
        : <input type="text" value={cur} placeholder={ph} onChange={(e) => set(e.target.value.trim())} />}
    </label>
  );
}

/* ---------- create a cluster ---------- */
async function clusterPreflight(f: Form) {
  const d = f.d;
  d._pf = { pending: true }; f.refresh();
  try { d._pf = await api("POST", "/api/clusters/preflight", { name: String(d.name || "").trim(), founder: +d.founder, joiners: d.joiners, links: d.links }); }
  catch (e: any) { d._pf = { error: e.message, ok: false, checks: [] }; }
  f.refresh();
}

export function newCluster() {
  const cand = DB.nodes.filter((n: any) => !n.cluster_id);
  if (!cand.length) { toast("No standalone devices", "Every device already belongs to a cluster", "warn"); return; }
  const members = (d: any) => [+d.founder, ...d.joiners];
  startWizard({
    title: "Create a Proxmox cluster", finishLabel: "Create cluster",
    data: { name: "", founder: cand[0].id, joiners: [], links: {}, _pf: null },
    steps: [
      { name: "Devices", render: (d, f) => <>
        <F.Text k="name" label="Cluster name" hint="Up to 15 letters, digits or “-”. It cannot be changed later." ph="edge-cluster-1" />
        <F.Sel k="founder" label="Founding device (creates the cluster; its guests stay)"
          opts={cand.map((n: any) => [n.id, `${n.name} — PVE ${n.pve_version || "?"} · ${n.arch || "?"} · ${guestsOn(n)} guests`] as [any, string])}
          onChange={() => { f.d._pf = null; f.refresh(); }} />
        <div className="section-title">Devices to join now (optional)</div>
        <p className="hint" style={{ marginTop: 0 }}>A joining device must have <b>no guests</b>, the same Proxmox major version and CPU architecture. Its storage.cfg and API token are replaced by the cluster's.</p>
        {(() => {
          const others = cand.filter((n: any) => String(n.id) !== String(d.founder));
          if (!others.length) return <p className="muted">No other standalone devices.</p>;
          return others.map((n: any) => {
            const on = d.joiners.includes(n.id);
            const g = guestsOn(n);
            return (
              <label key={n.id} className={"check " + (on ? "on" : "")}>
                <input type="checkbox" checked={on} onChange={(e) => {
                  const j = f.d.joiners; const i = j.indexOf(n.id);
                  if (e.target.checked) { if (i < 0) j.push(n.id); } else if (i >= 0) j.splice(i, 1);
                  f.d._pf = null; f.refresh();
                }} />
                <span><b>{n.name}</b><small>PVE {n.pve_version || "?"} · {n.arch || "?"} · {g} guest{g === 1 ? "" : "s"}{g ? " — must be emptied first" : ""}</small></span>
              </label>
            );
          });
        })()}
      </>,
        validate: (d) => (!/^[A-Za-z0-9][A-Za-z0-9-]{0,14}$/.test(String(d.name || "").trim()) ? "Cluster name: letters, digits and “-”, up to 15 characters." : null) },
      { name: "Links", render: (d, f) => {
        const ns = members(d).map(node).filter(Boolean);
        // old step.after: every device starts on its best address
        ns.forEach((n: any) => { if (!d.links[n.id]) d.links[n.id] = defaultLink(n); });
        return <>
          <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Pick the address corosync uses on each device (link0). Use a LAN every device can reach directly with low latency; a separate network for corosync is best.</p>
          {ns.map((n: any) => <LinkField key={n.id} f={f} n={n} label={n.name} ph="192.168.1.10" />)}
        </>;
      } },
      { name: "Preflight", render: (_d, f) => <PreflightStep f={f} run={clusterPreflight} intro />,
        validate: pfGate("Preflight found problems. Fix them (nothing was changed) and run it again.") },
      { name: "Review", render: (d) => {
        const nm = (i: any) => node(i)?.name ?? String(i);
        const steps = [
          "preflight again on every device",
          `pvecm create ${d.name} --link0 ${d.links[d.founder]}  (on ${nm(d.founder)})`,
          "wait for quorum",
          ...d.joiners.flatMap((i: any) => [
            `temporary SSH trust ${nm(i)} → ${nm(d.founder)}`,
            `pvecm add ${d.links[d.founder]} --link0 ${d.links[i]} --use_ssh  (on ${nm(i)})`,
            "remove the temporary trust · switch the console to the cluster API token · wait for quorum",
          ]),
          "re-read every member",
        ];
        return <>
          <dl className="kv"><dt>Cluster</dt><dd>{d.name}</dd>
            <dt>Founder</dt><dd>{nm(d.founder)} · link0 {d.links[d.founder]}</dd>
            <dt>Joining</dt><dd>{d.joiners.length ? d.joiners.map((i: any, k: number) => <span key={i}>{k ? <br /> : null}{nm(i)} · {d.links[i]}</span>) : "none (add devices later)"}</dd></dl>
          <div className="section-title">What will run</div>
          <pre className="log">{steps.map((x, k) => <span key={k}>{k ? "\n" : ""}<span className="g">▸</span> {x}</span>)}</pre>
          <div className="banner"><span>△</span><div><b>Hard to undo</b>Leaving a cluster means separating the node (the console can do it for an empty node) — plan the cluster before creating it.</div></div>
        </>;
      } },
    ],
    onFinish: (d) => job("POST", "/api/clusters", {
      name: String(d.name || "").trim(), founder: +d.founder, joiners: d.joiners,
      links: Object.fromEntries(Object.entries(d.links).map(([k, v]) => [k, v])),
    }),
  });
}

/* ---------- add a device to an existing cluster ---------- */
async function joinPreflight(f: Form, cid: any) {
  const d = f.d;
  d._pf = { pending: true }; f.refresh();
  try { d._pf = await api("POST", `/api/clusters/${cid}/join/preflight`, { node: +d.node, via: +d.via, links: d.links }); }
  catch (e: any) { d._pf = { error: e.message, ok: false, checks: [] }; }
  f.refresh();
}

export function joinCluster(cid: any, nodeId?: any) {
  const c = DB.clusters.find((x: any) => x.id === cid);
  const cand = DB.nodes.filter((n: any) => !n.cluster_id);
  const members = DB.nodes.filter((n: any) => n.cluster_id === cid);
  if (!cand.length) { toast("No standalone devices", "", "warn"); return; }
  if (!members.length) { toast("No member of this cluster is managed here", "Claim one of its devices first", "warn"); return; }
  const run = (f: Form) => joinPreflight(f, cid);
  startWizard({
    title: `Add a device to ${c?.name ?? "cluster"}`, finishLabel: "Add to cluster",
    data: { node: nodeId || cand[0].id, via: members[0].id, links: {}, _pf: null },
    steps: [
      { name: "Device", render: (d, f) => {
        const reset = () => { f.d._pf = null; f.refresh(); };
        const ns = [+d.via, +d.node].map(node).filter(Boolean);
        ns.forEach((n: any) => { if (!d.links[n.id]) d.links[n.id] = defaultLink(n); });
        return <>
          <F.Sel k="node" label="Device to add" onChange={reset}
            opts={cand.map((n: any) => [n.id, `${n.name} — PVE ${n.pve_version || "?"} · ${guestsOn(n)} guests`] as [any, string])} />
          <F.Sel k="via" label="Join through member" onChange={reset} opts={members.map((n: any) => [n.id, n.name] as [any, string])} />
          {ns.map((n: any, k: number) => <LinkField key={k + ":" + n.id} f={f} n={n} label={`Corosync address on ${n.name}`} />)}
        </>;
      } },
      { name: "Preflight", render: (_d, f) => <PreflightStep f={f} run={run} />,
        validate: pfGate("Preflight found problems; nothing was changed.") },
    ],
    onFinish: (d) => job("POST", `/api/clusters/${cid}/join`, { node: +d.node, via: +d.via, links: d.links }),
  });
}

/* ---------- leave a cluster ---------- */
function LeaveDlg({ n, c }: { n: any; c: any }) {
  const [name, setName] = useState("");
  const g = guestsOn(n);
  return (
    <Frame title={`Remove ${n.name} from ${c.name}?`} footer={<>
      <div className="spacer" />
      <button className="btn" onClick={Modal.close}>Cancel</button>
      <Btn danger id="delbtn" disabled={name.trim() !== n.name}
        onClick={() => job("POST", `/api/clusters/${c.id}/remove-node`, { node: n.id, confirm: name })}>Remove from cluster</Btn>
    </>}>
      {g ? <div className="banner"><span>△</span><div><b>{n.name} still has {g} guest(s)</b>Migrate them to another member first; the job refuses otherwise.</div></div> : null}
      <p style={{ marginTop: 0 }}>The console runs Proxmox's “separate a node without reinstalling” on {n.name} (stops corosync, removes the cluster config, restarts pve-cluster), then <code>pvecm delnode</code> on another member.
        {" "}{(c.members || []).length === 2 ? "The remaining node is set to expect one vote so it keeps quorum." : ""}</p>
      <label className="f"><span>Type <Copyable v={n.name} cls="strong" /> to confirm</span>
        <input type="text" id="delname" value={name} onChange={(e) => setName(e.target.value)} /></label>
    </Frame>
  );
}
export function leaveCluster(nid: any) {
  const n = node(nid), c = clusterOf(n);
  if (!n || !c) return;
  Modal.open(<LeaveDlg n={n} c={c} />);
}

/* ---------- live migration inside a cluster ---------- */
function MigrateDlg({ w, targets, stores }: { w: any; targets: any[]; stores: string[] }) {
  const f = useForm({ target: targets[0].id, ts: "" });
  return (
    <FormCtx.Provider value={f}>
      <Frame title={`Migrate ${w.name}`} footer={<>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        <Btn primary onClick={() => job("POST", `/api/workloads/${w.id}/migrate`, { target: +f.d.target, targetstorage: f.d.ts })}>Migrate</Btn>
      </>}>
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>{w.status === "running"
          ? (w.type === "vm" ? "Live migration: the VM keeps running while memory and local disks are copied." : "The container is stopped, moved and started again (LXC cannot migrate live).")
          : "The guest is stopped; it is moved as it is."}</p>
        <F.Sel k="target" label="Move to" opts={targets.map((n: any) => [n.id, `${n.name} — ${n.ram}% memory used`] as [any, string])} />
        <F.Sel k="ts" label="Target storage" opts={[["", "Same storage ID as now"] as [any, string], ...stores.map((s) => [s, s] as [any, string])]} />
      </Frame>
    </FormCtx.Provider>
  );
}
export function migrate(wid: any) {
  const w = wl(wid);
  if (!w) return;
  const src = node(w.node), c = clusterOf(src);
  if (!c) { toast("Not in a cluster", "Live migration needs both devices in one Proxmox cluster. Between standalone devices, restore a backup on the other device.", "warn"); return; }
  const targets = DB.nodes.filter((n: any) => n.cluster_id === c.id && n.id !== src.id && n.status !== "offline");
  if (!targets.length) { toast("No other online member", "", "warn"); return; }
  const stores = (n: any): string[] => (n.storage || [])
    .filter((s: any) => s.active && (s.content || "").match(w.type === "vm" ? /images/ : /rootdir/)).map((s: any) => s.id);
  Modal.open(<MigrateDlg w={w} targets={targets} stores={stores(targets[0])} />);
}
